<script lang="ts" setup>
import { useToast } from 'vue-toastification';
import ToastComponent from '~/components/ui/ToastComponent.vue';
import type { OfferBalance, OfferMedium, OfferSmall } from '~/types/Catalog';

const props = defineProps<{ offer: OfferMedium; recommendationsBlockId?: string }>();

const cartStore = useCartStore();
const route = useRoute();

const isShowModal = ref(false);
const currentOffer = ref(props.offer.offers.find((o) => o.id === props.offer.id) ?? props.offer.offers[0]);

const offerForDataLayer = computed(() => ({
  ...currentOffer.value,
  brand: props.offer.brand,
  displayable_offer_properties: props.offer.displayable_offer_properties,
  price: props.offer.price,
}));

const isCurrentOfferContainSizes = computed(() =>
  currentOffer.value.displayable_offer_properties.some((prop) => ['Размер основной', 'Размер'].includes(prop.name)),
);

onMounted(() => {
  if (props.offer.offers.length && isCurrentOfferContainSizes.value) {
    const parentOfferColor = getOfferColor(currentOffer.value);

    for (const i in props.offer.offers) {
      if (props.offer.offers[i] && getOfferColor(props.offer.offers[i]) === parentOfferColor) {
        const isOfferInCart = cartStore.countedCartItems.map((item) => item.id).includes(props.offer.offers[i].id);

        if (isOfferInCart) {
          setCurrentOffer(props.offer.offers[i]);
          break;
        }
      }
    }
  }
});

function setCurrentOffer(offer: OfferSmall) {
  currentOffer.value = offer;
}

const countInCart = computed(() => {
  const currentOfferInCart = cartStore.countedCartItems.find((item) => item.id === currentOffer.value.id);

  return currentOfferInCart?.count ?? 0;
});

const currentOfferAvailableCount = computed(() => {
  let count = 0;

  currentOffer.value.offer_balances.forEach((balance: OfferBalance) => {
    if (balance.warehouse.delivery_available) count += balance.balance;
  });

  return count;
});

function checkIsAvailableOnlyOffline(offer: OfferSmall) {
  let count = 0;

  offer.offer_balances.forEach((balance: OfferBalance) => {
    if (balance.warehouse.delivery_available) count += balance.balance;
  });

  return count < 1 && offer.offer_balances.some((balance: OfferBalance) => balance.balance > 0);
}

const isNotAvailableToIncrement = computed(() => countInCart.value >= currentOfferAvailableCount.value);

const isLoading = ref('');
const { sendOfferEventToDataLayer } = useGtmModule();

async function onModalConfirm() {
  if (isLoading.value) return;

  isShowModal.value = false;
  isLoading.value = 'increment';

  const isAddedToCart = await cartStore.addToCart(currentOffer.value.id);

  if (isAddedToCart) {
    await sendOfferEventToDataLayer('add_to_cart', offerForDataLayer.value as unknown as OfferMedium);
  }

  isLoading.value = '';
}

const filteredByColorAndSortedBySizeOffers = computed(() => {
  let result: OfferSmall[];

  const selectedColor = getOfferColor(currentOffer.value);

  result = props.offer.offers.filter((offer) => getOfferColor(offer) === selectedColor);

  if (result.every((offer) => offer.size_charts_values?.length)) {
    result.sort((a, b) => a.size_charts_values[0].order - b.size_charts_values[0].order);
  }

  return result;
});

async function addToCart() {
  if (countInCart.value) return navigateTo(props.offer.uri);

  const showNotAvailableToast = () => {
    const toast = useToast();

    toast.error({
      component: ToastComponent,
      props: { text: 'Товар доступен только для покупки в розничном магазине' },
    });
  };

  if (isCurrentOfferContainSizes.value) {
    const availableSizes = [];

    for (const offer of filteredByColorAndSortedBySizeOffers.value) {
      const isAvailableOnlyOffline = checkIsAvailableOnlyOffline(offer);

      if (!isAvailableOnlyOffline) availableSizes.push(offer);
    }

    if (availableSizes.length) {
      setCurrentOffer(availableSizes[0]);
      isShowModal.value = true;
    } else {
      showNotAvailableToast();
      return;
    }
  } else {
    if (checkIsAvailableOnlyOffline(currentOffer.value)) {
      showNotAvailableToast();
      return;
    }

    if (isLoading.value) return;

    isLoading.value = 'increment';

    const isAddedToCart = await cartStore.addToCart(currentOffer.value.id);

    if (isAddedToCart) {
      await sendOfferEventToDataLayer('add_to_cart', offerForDataLayer.value as unknown as OfferMedium);
      window.roistat?.event?.send('add_to_cart');
      if (window.r46) {
        const blockId = props.recommendationsBlockId ?? route.query.recommended_code;
        const params = blockId
          ? { id: currentOffer.value.id, recommended_by: 'dynamic', recommended_code: blockId, amount: 1 }
          : { id: currentOffer.value.id, amount: 1 };

        window.r46('track', 'cart', params);
      }
    }

    isLoading.value = '';
  }
}

async function incrementCountInCart() {
  if (isLoading.value) return;

  isLoading.value = 'increment';

  const isSuccess = await cartStore.increment(currentOffer.value.id);

  if (isSuccess) {
    await sendOfferEventToDataLayer('add_to_cart', offerForDataLayer.value as unknown as OfferMedium);
    if (window.r46) {
      const item = cartStore.countedCartItems.find((i) => i.id === currentOffer.value.id);
      const blockId = props.recommendationsBlockId ?? route.query.recommended_code;
      const params = blockId
        ? { id: currentOffer.value.id, recommended_by: 'dynamic', recommended_code: blockId, amount: item?.count }
        : { id: currentOffer.value.id, amount: item?.count };

      window.r46('track', 'cart', params);
    }
  }

  isLoading.value = '';
}

async function decrementCountInCart() {
  if (isLoading.value) return;

  isLoading.value = 'decrement';

  const isSuccess = await cartStore.decrement(currentOffer.value.id);

  if (isSuccess) {
    if (countInCart.value === 0) {
      await sendOfferEventToDataLayer('remove_from_cart', offerForDataLayer.value as unknown as OfferMedium);
      window.roistat?.event?.send('remove_from_cart');
    }
  }

  isLoading.value = '';
}

function getOfferColor(offer: OfferSmall): string {
  return offer.displayable_offer_properties.find((p) => p.name === 'Цвет')?.value ?? '';
}
</script>

<template>
  <component
    :is="countInCart ? 'div' : 'button'"
    :class="{ 'product-card-footer--in-cart': countInCart }"
    :disabled="Boolean(isLoading)"
    class="product-card-footer"
    @click.stop.prevent="addToCart"
  >
    <div class="flex flex-col items-start gap-1">
      <div class="font-bold">{{ offer.price.toLocaleString('ru-RU') }}&nbsp;₽</div>

      <div
        v-if="offer.old_price"
        :class="{ '!text-hell-black-40': countInCart }"
        class="line-through text-hell-black-60 text-xs sm:text-sm !leading-none"
      >
        {{ offer.old_price.toLocaleString('ru-RU') }}&nbsp;₽
      </div>
    </div>

    <div class="flex justify-center items-center">
      <Transition
        v-if="countInCart === 0 && Boolean(isLoading)"
        class="w-5 ml-1"
        name="loader"
      >
        <SvgoSpinner
          class="animate-spin"
          title="Идет загрузка..."
        />
      </Transition>

      <template v-if="countInCart">
        <ProductCardCounterBlock
          :count-in-cart="countInCart"
          :is-loading="isLoading"
          :is-not-available-to-increment="isNotAvailableToIncrement"
          @decrement="decrementCountInCart"
          @increment="incrementCountInCart"
        />
      </template>

      <div
        v-else
        class="product-card-footer__add-to-cart-icon"
      >
        <SvgoCart
          class="text-xl"
          filled
        />
      </div>

      <BaseModal
        v-model="isShowModal"
        confirm-btn-icon="Cart"
        confirm-btn-text="В корзину"
        title="Варианты"
        @close="isShowModal = false"
        @confirm="onModalConfirm"
      >
        <div class="flex items-center gap-4">
          <picture
            v-if="offer.offer_images[0].url"
            class="cart-list-item__image"
          >
            <source
              v-if="offer.offer_images[0].conversions_webp.thumb_url_180"
              :srcset="`${offer.offer_images[0].conversions_webp.thumb_url_180}, ${offer.offer_images[0].conversions_webp.thumb_url_360} 2x`"
              media="(max-width: 360px)"
              type="image/webp"
            />

            <source
              v-if="offer.offer_images[0].conversions_webp.thumb_url_360"
              :srcset="`${offer.offer_images[0].conversions_webp.thumb_url_360}, ${offer.offer_images[0].conversions_webp.thumb_url_740} 2x`"
              type="image/webp"
            />

            <source
              v-if="offer.offer_images[0].conversions.thumb_url_360"
              :srcset="offer.offer_images[0].conversions.thumb_url_360"
              type="image/jpeg"
            />

            <img
              :alt="offer.product_name"
              :src="offer.offer_images[0].url"
              class="w-full h-full object-contain"
              loading="lazy"
            />
          </picture>

          <span class="flex-shrink">{{ offer.product_name }}</span>

          <span class="flex-shrink-0 font-bold">{{ offer.price.toLocaleString('ru-RU') }} ₽</span>
        </div>

        <ul class="p-0">
          <li
            v-for="smallOffer in filteredByColorAndSortedBySizeOffers"
            :key="smallOffer.id"
            :class="{
              'font-bold': smallOffer.id === currentOffer.id,
              'product-card-footer__offer-list-item--disabled':
                checkIsAvailableOnlyOffline(smallOffer) ||
                smallOffer.offer_balances.every((balance) => balance.balance === 0),
            }"
            class="product-card-footer__offer-list-item"
            @click="setCurrentOffer(smallOffer)"
          >
            <button>
              {{
                smallOffer.displayable_offer_properties.find((prop) =>
                  ['Размер основной', 'Размер'].includes(prop.name),
                )?.value
              }}
            </button>

            <SvgoCheck
              v-if="smallOffer.id === currentOffer.id"
              class="text-2xl"
              filled
            />
          </li>
        </ul>
      </BaseModal>
    </div>
  </component>
</template>

<style lang="scss" scoped>
.product-card-footer {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  justify-content: space-between;
  align-items: center;
  padding: 4px 6px;
  border: 1px solid $hell-black-10;
  border-radius: 8px;
  font-size: $text-sm;
  transition: all 0.3s;

  @include screen-xs {
    gap: 8px;
    min-height: 40px;
  }

  @include screen-sm {
    gap: 16px;
  }

  @include screen-md {
    font-size: $text-base;
  }

  &--in-cart {
    border-color: $hell-green !important;
    background-color: $hell-green !important;
    color: $white;
  }

  &__add-to-cart-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 32px;
    height: 32px;
    border-radius: 100%;
    background-color: transparent;
  }

  &__offer-list-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 44px;
    padding: 1rem 0;
    border-bottom: 1px solid $hell-black-20;
    cursor: pointer;
    transition: color 0.3s;

    &:hover {
      color: $hell-black-60;
    }

    &--disabled {
      color: $hell-black-40;
      cursor: not-allowed;
      pointer-events: none;
    }
  }
}
</style>
